import React from 'react'
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormDateControl } from '@agtech/web/components/Forms/Controls/AgTechFormDateControl';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import { Sale, DefaultSale, SaleFeatures } from '@sales/data/sales/SaleData';
import { SaleCreationResult, useSaleDataWriteSaleAction } from '@sales/data/sales/SaleDataActions';
import dayjs from 'dayjs';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { SaleOperationDataGetLicensedProductsQuery } from '@sales/data/operation/SaleDataOperationQueries';
import { ProductDTO } from '@agtech/data/operations/OperationData';
import AgTechSelectableCard from '@agtech/web/components/Cards/AgTechSelectableCard';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { useAgTechWebAppContext } from '@agtech/web/components/App/Contexts/AgTechWebAppContext';
import SalePaymentModal from '../modals/SalePaymentModal';
import { useAgTechFeatureContext } from '@agtech/core/features/AgTechFeatures';

export type SaleDialogProps = {
    sale?: Sale,
    onSaleSubmitted?: (writtenSale: Sale) => Promise<void>
}

type SaleDialogFormData = {
    sale: Sale,
    selectedProduct: ProductDTO
}

const SaleDialog = (props: SaleDialogProps) => {
    const appContext = useAgTechWebAppContext();
    const dialogQuery = useSaleDialogQuery();
    const dialogAction = useSaleDataWriteSaleAction();
    const featureContext = useAgTechFeatureContext();

    const isSaleLicensingEnabled = featureContext.isFeatureEnabled(SaleFeatures.EnforceLicensing);

    const onSaleSubmitted = async (result: SaleCreationResult) => {
        const isCreatingNewSale = props.sale === undefined;
        const wasProductSelected = result.purchaseIntent?.product !== undefined;

        if (isSaleLicensingEnabled && isCreatingNewSale && wasProductSelected) {
            await new Promise<void>((res) => {
                appContext.modals.showModal({
                    content: () => (
                        <SalePaymentModal
                            {...result}
                            callbacks={{
                                onSuccessfulPayment: async () => { res() }
                            }}
                        />
                    ),
                    showCloseButton: false
                });
            });
        }

        await props.onSaleSubmitted?.(result.sale);

        return true;
    }

    return (
        <AgTechFormDialog
            width={700}
            dialogData={{
                query: dialogQuery,
                getDialogData: d => {
                    const dialogData: SaleDialogFormData = {
                        sale: props.sale ?? DefaultSale,
                        selectedProduct: d[0]
                    };

                    return dialogData;
                },
                getDialogContext: d => ({
                    products: d
                })
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: (formEntity) => ({
                    sale: formEntity.sale,
                    productId: formEntity.selectedProduct?.id,
                    onSaleCreated: onSaleSubmitted
                })
            }}
            structure={() => ({
                header: props.sale ? 'Update Sale' : 'Create Sale',
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <AgTechFormSection header="General">
                        <AgTechFormRow>
                            <AgTechFormTextControl 
                                entityType={formContext.entity}
                                focused={true} 
                                required={true} 
                                caption="Name" 
                                value={d => d.sale.saleName} 
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    sale: {
                                        ...e.sale,
                                        saleName: val
                                    }
                                })}
                                width={100}
                            />
                            <AgTechFormDateControl
                                entityType={formContext.entity}
                                caption="Start Date" 
                                value={d => dayjs(d.sale.saleStartDate).format('MM-DD-YYYY')} 
                                width={50} 
                                allowPreviousDates={false}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    saleStartDate: dayjs(val).toDate()
                                })}
                            />
                            <AgTechFormDateControl
                                entityType={formContext.entity}
                                caption="End Date" 
                                value={d => dayjs(d.sale.saleEndDate).format('MM-DD-YYYY')} 
                                width={50} 
                                allowPreviousDates={false}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    saleEndDate: dayjs(val).toDate()
                                })}
                            />
                        </AgTechFormRow>
                    </AgTechFormSection>
                    {isSaleLicensingEnabled && formContext.context.products && (
                        <AgTechFormSection header='Product selection'>
                            <div className='flex-row gap-x-3 wide mt-2 flex-wrap flex'>
                                {formContext.context.products.map((product, index) => {
                                    return (
                                        <AgTechSelectableCard
                                            key={index}
                                            header={product.productName}
                                            detail={product.productDescription}
                                            isSelected={formContext.entity.selectedProduct?.id === product.id}
                                            onSelected={async () => {
                                                formContext.updateFormEntity(e => ({
                                                    ...e,
                                                    selectedProduct: product
                                                }))
                                            }}
                                            structure={{ width: 200}}
                                            body={() => (
                                                <div className='flex-col flex'>
                                                    <h1 className='text-2xl font-semibold'>
                                                        ${product.cost.toFixed(2)}
                                                    </h1>
                                                </div>
                                            )}
                                        />
                                    )
                                })}
                            </div>
                        </AgTechFormSection>
                    )}
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

const useSaleDialogQuery = () => {
    return useAgTechQuery({
        query: SaleOperationDataGetLicensedProductsQuery
    });
}

SaleDialog.Open = (e: AgTechClickEventHandler, props: SaleDialogProps) => {
    return e.dialogs.openDialog({
        dialog: () => <SaleDialog {...props} />
    });
}

SaleDialog.Button = (props: AgTechButtonProps & SaleDialogProps) => {
    return (
        <AgTechButton
            text={props.sale ? 'Update Sale' : 'Create Sale'}
            icon={props.sale ? 'edit' : 'plus'}
            {...props}
            action={e => SaleDialog.Open(e, props)}
        />
    )
}

export default SaleDialog